import axios from 'axios';

import crypto_js from 'crypto-js';
import { SERVER_ERROR_CODES } from '@constants';

/**
 * Provide a layer over the axios library, to
 * configure request parameters before making a HTTP call.
 *
 * NOTE: This class shouln't be used directly,
 * use api object from api.js
 */
class Request {
  constructor() {
    // The reason why we are not directly use the env variables
    // is to have clear picture what keys are present in configuration
    this.configuration = {
      baseUrl: process.env.REACT_APP_BASE_URL,
      basePath: process.env.REACT_APP_BASE_PATH || "",
      apiTimeOverAllowance: process.env.REACT_APP_API_TIMEOVER_ALLOWANCE,
      s3TimeOverAllowance: process.env.REACT_APP_S3_TIMEOVER_ALLOWANCE,
      secretKey: process.env.REACT_APP_SECRET_KEY,
    };
  }

  /**
   * Initilization at app start up
   */
  init({
    lang,
    sessionExpiredAction = () => { },
    accountLockedAction = () => { },
    systemMaintainAction = () => { },
  }) {
    this.configuration.lang = lang;
    this.sessionExpiredAction = sessionExpiredAction;
    this.accountLockedAction = accountLockedAction;
    this.systemMaintainAction = systemMaintainAction;
  }

  post(path, params, data = {}, isMultipart = false, presignedUrl) {
    return this.send({
      path,
      method: 'POST',
      params,
      data,
      isMultipart,
      presignedUrl,
    });
  }

  put(path, params, data) {
    return this.send({ path, method: 'PUT', params, data });
  }

  get(path, params, data) {
    return this.send({ path, method: 'GET', params, data });
  }

  delete(path, params, data) {
    return this.send({ path, method: 'DELETE', params, data });
  }

  send({
    /**
     * `path` is the api endpoint that will be appended
     * with base_url + root_path, to form complete url
     */
    path,
    /**
     * `method` is the HTTP request method to indicate
     * the desired action to be performed for a given resource
     *
     * https://developer.mozilla.org/en-US/docs/Web/HTTP/Methods
     */
    method,
    /**
     * `params` are the URL GET parameters to be appended with the url.
     * Must be a plain object or a URLSearchParams object
     */
    params = {},
    /**
     * `data` is the data to be sent as the request body
     * Only applicable for request methods 'PUT', 'POST', and 'PATCH'
     * When no `transformRequest` is set, must be of one of the following types:
     * - string, plain object, ArrayBuffer, ArrayBufferView, URLSearchParams
     * - Browser only: FormData, File, Blob
     * - Node only: Stream, Buffer
     */
    data,
    /**
         *  true for multipart request
         */
    isMultipart = false,
    /**
     * AWS S3 presigned url
     */
    presignedUrl,
  }) {
    const url = isMultipart ? presignedUrl : `${this.configuration.baseUrl}${this.configuration.basePath}${path}`;

    const headers = isMultipart
      ? {}
      : {
        'Content-Type': 'text/plain',
      };

    const dynamicFields = this.configuration.token
      ? { token: this.configuration.token }
      : {};

    data = isMultipart
      ? data
      : {
        ...data,
        ...dynamicFields,
        lang: this.configuration.lang,
      };

    // Encrypt
    data = isMultipart ? data : this.encrypt(data);

    const timeout = isMultipart
      ? this.configuration.s3TimeOverAllowance
      : this.configuration.apiTimeOverAllowance;

    return new Promise((resolve, reject) => {
      axios({
        url,
        method,
        timeout,
        headers,
        params,
        data,
      })
        .then((response) => {
          if (isMultipart) {
            resolve();
          }
          if (response.data) {
            // Decrypt
            let _response = this.decrypt(response?.data);
            resolve(_response);
          }
          resolve();
        })
        .catch((error) => {
          if (error?.response) {
            const {
              status,
              data,
            } = error?.response;

            if (status === SERVER_ERROR_CODES.ACCOUNT_LOCKED) {
              this.handleAccountLock();
            }
            if (status === SERVER_ERROR_CODES.SESSION_EXPIRED) {
              this.handleSessionExpire();
            }

            // Decrypt
            let _response = this.decrypt(data);
            let message = _response?.errors[0]?.message;
            let statusCode = _response?.statusCode;

            if (status === SERVER_ERROR_CODES.SYSTEM_MAINTAIN) {
              this.handleSystemMaintain(message);
            }

            if (
              statusCode === SERVER_ERROR_CODES.TOO_MANY_REQUEST ||
              statusCode === SERVER_ERROR_CODES.INACTIVE_PROMOCODE
            ) {
              reject({
                message,
                statusCode,
              });
            }
            reject(message);
          }
          reject();
        });
    });
  }

  encrypt(data) {
    return crypto_js
      .AES
      .encrypt(
        JSON.stringify(data),
        this.configuration.secretKey
      )
      .toString();
  }

  decrypt(data) {
    try {
      return JSON.parse(crypto_js.AES.decrypt(data, this.configuration.secretKey).toString(crypto_js.enc.Utf8));

    } catch {
      return { errors: [] };
    }
  }

  setSessionToken(token) {
    this.configuration.token = token;
  }

  setUserPreferedLanguage(lang) {
    this.configuration.lang = lang;
  }

  handleSessionExpire() {
    /**
     * Dispatch a redux action, notifying session has expired.
     *
     * This function was attached to request object at the
     * time of "INITIALIZE_APP" process.
     */
    this.sessionExpiredAction();
  }

  handleAccountLock() {
    /**
     * Dispatch a redux action, notifying account has locked.
     *
     * This function was attached to request object at the
     * time of "INITIALIZE_APP" process.
     */
    this.accountLockedAction();
  }

  handleSystemMaintain(message) {
    /**
     * Dispatch a redux action, notifying about system maintenance.
     *
     * This function was attached to request object at the
     * time of "INITIALIZE_APP" process.
     */
    this.systemMaintainAction(message);
  }

}

export default Request;
